'use client';
import React from 'react';
import Link from 'next/link';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PlatformIcon } from '@/components/ConnectedAccountCard';
import { isZendeskIntegrationEnabled } from '@/lib/features/isZendeskIntegrationEnabled';

const PLATFORM_IDS_BASE = [
  'instagram', 'tiktok', 'youtube', 'google', 'linkedin', 'github', 'twitter', 'facebook',
  'pinterest', 'whatsapp', 'telegram', 'reddit', 'snapchat', 'discord', 'twitch', 'vimeo',
  'dribbble', 'dropbox', 'canva', 'google_drive', 'slack', 'wechat', 'mastodon', 'substack',
] as const;

const PLATFORM_IDS = [
  ...PLATFORM_IDS_BASE.slice(0, PLATFORM_IDS_BASE.indexOf('slack') + 1),
  ...(isZendeskIntegrationEnabled() ? (['zendesk'] as const) : []),
  ...PLATFORM_IDS_BASE.slice(PLATFORM_IDS_BASE.indexOf('slack') + 1),
];

export default function PlatformCarousel({ size = 48, speed = 18 }: { size?: number; speed?: number }) {
  const theme = useTheme();

  const renderSegment = (suffix: string, opts: { role: 'list' | 'presentation'; ariaHidden?: boolean }) => (
    <Box
      className="pf-segment"
      component="div"
      role={opts.role}
      aria-hidden={opts.ariaHidden}
      sx={{ display: 'flex', gap: 4, alignItems: 'center' }}
    >
      {PLATFORM_IDS.map((id) => {
        const label =
          id === 'twitter'
            ? 'X'
            : id === 'google_drive'
              ? 'Google Drive'
              : id === 'canva'
                ? 'Canva'
                : id === 'zendesk'
                  ? 'Zendesk'
                : id.charAt(0).toUpperCase() + id.slice(1);
        return (
          <Box className="pf-item" component="div" role="listitem" key={`${id}-${suffix}`} sx={{ minWidth: size + 24 }}>
            <Link href={`/tools?platform=${encodeURIComponent(id)}`} aria-label={`Open tools for ${label}`} className="pf-link" tabIndex={opts.ariaHidden ? -1 : 0}>
              <PlatformIcon platformId={id as any} size={size} />
            </Link>
            <Typography fontSize={11} sx={{ mt: 0.5, opacity: 0.9, color: 'text.secondary' }}>
              {label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box
      sx={{ width: '100%', overflow: 'hidden' }}
      role="region"
      aria-label="Supported social platforms"
      aria-roledescription="carousel"
    >
      <Box component="p" sx={visuallyHidden}>
        A scrolling row of platform logos. Each logo links to FloAI tools filtered for that platform. Motion can be paused with reduced motion settings.
      </Box>
      <style>{`
        @keyframes scrollLeft {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .pf-track {
          display: flex;
          gap: 32px;
          align-items: center;
          white-space: nowrap;
          will-change: transform;
          animation: scrollLeft ${speed}s linear infinite;
        }
        .pf-item {
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        @media (prefers-reduced-motion: reduce) {
          .pf-track { animation: none; }
        }
        .pf-link { outline: none; border-radius: 8px; padding: 4px; display: inline-flex; align-items: center; justify-content: center; }
        .pf-link:focus-visible { box-shadow: 0 0 0 3px ${theme.palette.mode === 'dark' ? alpha(theme.palette.primary.light, 0.5) : alpha(theme.palette.primary.main, 0.35)}; }
      `}</style>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <div className="pf-track">
          {renderSegment('a', { role: 'list' })}
          {renderSegment('b', { role: 'presentation', ariaHidden: true })}
        </div>
      </Box>
    </Box>
  );
}

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;
